import { FormattedMessage, useIntl } from "react-intl";
import type { MatchMode } from "./design-codec";

// The creation-time mode choice (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@1 §4, spark #136 D3). Formerly a
// standalone modal that *was* the creation act; now a selectable card grid
// embedded as the first section of the new-match settings dialog so the whole
// creation happens in one popup. The mode is picked once for a fresh match and
// becomes read-only after the match has content (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@1: immutable after
// creation).
const MODES: Array<{
  mode: MatchMode;
  title: { id: string; defaultMessage: string };
  slogan: { id: string; defaultMessage: string };
  output: { id: string; defaultMessage: string };
}> = [
  {
    mode: "OPTIMIZE",
    title: { id: "match.modePicker.optimizeTitle", defaultMessage: "Optimize team" },
    slogan: {
      id: "match.modePicker.optimizeSlogan",
      defaultMessage: "Compose the best team from a pool.",
    },
    output: { id: "match.modePicker.optimizeOutput", defaultMessage: "one team" },
  },
  {
    mode: "ASSESS",
    title: { id: "match.modePicker.assessTitle", defaultMessage: "Assess team" },
    slogan: {
      id: "match.modePicker.assessSlogan",
      defaultMessage: "Check a given team against the requirements.",
    },
    output: { id: "match.modePicker.assessOutput", defaultMessage: "verdict + gaps" },
  },
  {
    mode: "RANK",
    title: { id: "match.modePicker.rankTitle", defaultMessage: "Rank candidates" },
    slogan: {
      id: "match.modePicker.rankSlogan",
      defaultMessage: "Rank candidates for an open position.",
    },
    output: { id: "match.modePicker.rankOutput", defaultMessage: "ranked list A/B/C" },
  },
];

export function ModeSelect({
  value,
  onChange,
  disabled = false,
}: {
  value: MatchMode;
  onChange: (mode: MatchMode) => void;
  disabled?: boolean;
}) {
  const intl = useIntl();
  return (
    <div
      className="grid gap-3 @sm:grid-cols-3"
      role="radiogroup"
      aria-label={intl.formatMessage({ id: "match.modePicker.title" })}
    >
      {MODES.map((m) => (
        <ModeCard
          key={m.mode}
          {...m}
          selected={m.mode === value}
          onPick={() => onChange(m.mode)}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

function ModeCard({
  title,
  slogan,
  output,
  selected,
  onPick,
  disabled,
}: (typeof MODES)[number] & { selected: boolean; onPick: () => void; disabled: boolean }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onPick}
      disabled={disabled}
      className={`box-flat bg-page flex flex-col gap-3 p-4 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30 disabled:cursor-default ${
        selected ? "ring-2 ring-ink" : "hover:ring-1 hover:ring-ink/30"
      } ${disabled && !selected ? "opacity-50" : ""}`}
    >
      <span className="font-serif text-lg leading-none">
        <FormattedMessage {...title} />
      </span>
      <span className="text-[13px] leading-snug text-ink/70">
        <FormattedMessage {...slogan} />
      </span>
      <span className="field-label mt-auto">
        <FormattedMessage id="match.modePicker.outputLabel" defaultMessage="Result" />
        {": "}
        <FormattedMessage {...output} />
      </span>
    </button>
  );
}
